import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { Component, NgModule, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from './service/auth.service';
import { AuthStateService } from './service/auth-state.service';
import { WebSocketService } from './service/websocket.service';
import { NotificationService } from './service/notification.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ShortcutService } from './service/shortcut.service';
import { PushNotificationService } from './service/push-notification.service';
import { BarcodeScannerService } from './service/barcode-scanner.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  title = 'my-app';


  constructor(
    private authService: AuthService,
    private authStateService: AuthStateService,
    private webSocketService: WebSocketService,
    private router: Router,
    private shortcutService: ShortcutService,
    private pushNotificationService: PushNotificationService,
    private notificationService: NotificationService,
    private barcodeScannerService: BarcodeScannerService
  ) {
    registerLocaleData(localeFr, 'fr');
  }

  ngOnInit() {
    console.log('🚀 [APP] Initializing application...');

    // Initialize Shortcut Service
    this.shortcutService.init();

    // Initialize global barcode scanner (intercepts scans app-wide)
    this.barcodeScannerService.init();

    // Initialize WebSocket connection for real-time notifications
    this.initializeWebSocket();

    // Start periodic token expiration check
    this.authService.startTokenExpirationCheck();

    // Listen to route changes to handle authentication checks
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.handleRouteChange(event.url);
      }
    });

    // Subscribe to authentication state changes
    this.authStateService.isAuthenticated$.subscribe(isAuthenticated => {
      console.log('🔐 [APP] Authentication state changed:', isAuthenticated);
      this.handleAuthenticationStateChange(isAuthenticated);
    });
  }

  private initializeWebSocket(): void {
    console.log('🔌 [APP] Initializing WebSocket connection for notifications');

    // Subscribe to individual notifications to ensure they are received
    this.webSocketService.getIndividualNotifications().subscribe({
      next: (notification) => {
        if (notification) {
          console.log('🔌 [APP] WebSocket notification received (handled by nav-bar):', notification.type);
        }
      },
      error: (error) => {
        console.error('🔌 [APP] Error receiving WebSocket notification:', error);
      }
    });
  }

  /**
   * Determine if notification type should trigger native Windows notification
   */
  private isCriticalNotification(type: string): boolean {
    const criticalTypes = [
      'CAMERA_VERIFICATION_URGENT',
      'RECONCILIATION_DANGER',
      'RECONCILIATION_WARNING',
      'SECURITY_ALERT'
    ];
    return criticalTypes.includes(type);
  }

  /**
   * Map WebSocket notification type to in-app toast type
   */
  private mapNotificationType(type: string): 'success' | 'info' | 'warning' | 'error' {
    if (type === 'RECONCILIATION_DANGER' || type === 'CAMERA_VERIFICATION_URGENT' || type === 'SECURITY_ALERT') {
      return 'error';
    } else if (type === 'RECONCILIATION_WARNING') {
      return 'warning';
    } else if (type === 'RECONCILIATION_SUCCESS') {
      return 'success';
    }
    return 'info';
  }

  private handleAuthenticationStateChange(isAuthenticated: boolean): void {
    if (isAuthenticated) {
      const isRememberMe = this.authService.isRememberMeSession();
      console.log('🔐 [APP] User authenticated');
      if (isRememberMe) {
        console.log('🔐 [APP] Remember me session active (7 days)');
      } else {
        console.log('🔐 [APP] Regular session active (24 hours)');
      }

      // [ADDITIVE] Initialize push notifications for mobile support
      this.initializePushNotifications();
    } else {
      console.log('🔐 [APP] User not authenticated');
    }
  }

  /**
   * Initialize push notifications for mobile devices.
   * This is ADDITIVE and does NOT affect WebSocket notifications.
   */
  private async initializePushNotifications(): Promise<void> {
    try {
      const success = await this.pushNotificationService.initializePushNotifications();
      if (success) {
        console.log('📱 [PUSH] ✅ Push notifications initialized successfully');
      } else {
        console.log('📱 [PUSH] Push notifications not available or permission denied');
      }
    } catch (error) {
      console.error('📱 [PUSH] Failed to initialize push notifications:', error);
    }
  }

  private handleRouteChange(url: string): void {
    // Allow unauthenticated access to these routes
    const publicRoutes = ['/login', '/signup', '/verify-email', '/forgot-password', '/reset-password'];
    const isPublicRoute = publicRoutes.some(route => url.startsWith(route));

    if (isPublicRoute) {
      console.log('🔐 [APP] Allowing access to public route:', url);
      return;
    }

    // For protected routes, check authentication using the state service
    this.authStateService.isAuthenticated$.subscribe(isAuthenticated => {
      if (!isAuthenticated) {
        console.log('🔐 [APP] User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
      }
    });
  }
}
